import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import { describeSlackLinkError } from '../../common/slackCallbackResult';

/**
 * Reports the outcome of the per-user "Sign in with Slack" callback, wherever it lands.
 *
 * Mounted once in the authorized layout rather than on the settings page, because the
 * callback has two landing sites: the organization's notifications settings when
 * membership was re-verified, and the SPA root for the two outcomes reached before it
 * was (`user_mismatch`, `not_authorized`). A notice owned by the settings page would
 * never run for those two, which are precisely the ones a user cannot diagnose alone.
 *
 * The parameters are consumed, not just read: they are stripped from the URL once
 * shown, so a refresh or a shared link does not replay a result from a flow that is
 * already over. `replace` keeps the stripped URL out of the history stack, so Back
 * still goes where the user expects.
 */
export default function SlackCallbackNotice(): null {
  const [searchParams, setSearchParams] = useSearchParams();

  const linked = searchParams.get('slack');
  const error = searchParams.get('slack_error');

  useEffect(() => {
    if (!linked && !error) return;

    if (error) {
      void message.error(describeSlackLinkError(error), 6);
    } else if (linked === 'linked') {
      void message.success('Your Slack account is linked.');
    }

    const next = new URLSearchParams(searchParams);
    next.delete('slack');
    next.delete('slack_error');
    setSearchParams(next, { replace: true });
    // `searchParams` and `setSearchParams` are deliberately absent: react-router hands
    // back a new object identity on every render, so including them would re-run this
    // effect after its own setSearchParams and loop. The two values it actually reads
    // are in the list.
  }, [linked, error]);

  return null;
}
